import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route } from 'react-router-dom'
import { Switch } from 'react-router'
import Actions from '../../../actions'
import ListView from '../../common/ListView'
import dayjs from 'dayjs'
import $t from '../../../i18n'
import { useRouteMatch } from 'react-router'
import FilePreviewPage from '../FilePreviewPage'
import Page from '../../common/Page'
import ActionButton from '../../common/ActionButton/ActionButton'
import { BenefitTypeNames, SSO } from 'consts/coverageBenefitsConsts'
import { routes } from '../../../consts'
import SSOAlert from '../../common/SSOAlert'
import { event, eventTypes } from '../../../consts/eventConsts'
import { HMOProviders } from 'consts'
import {
  isMemberCanceled,
  isMemberInsuranceNotActivated,
  isMemberTermed,
} from '../../../utils/hmoUtils'

import Other from '../../../resources/Otherprescriptions.png'
import Reimbursement from '../../../resources/reimbursement.png'
import Pharmacy from '../../../resources/prefferedPharmacy.png'

import './prescription.css'

const Prescription = () => {
  const dispatch = useDispatch()
  const { path, url } = useRouteMatch()
  const moreOptionRef = useRef(null)
  const authenticatedLinks = useSelector(
    state => state.profile?.authenticatedLinks
  )
  const selectedMemberId = useSelector(
    state => state.account?.selectedMember?.id
  )

  const prescriptions = useSelector(
    state => state.medicalInfo?.currentPrescriptions
  )
  const isLoading = useSelector(state => state.medicalInfo?.isLoadingDocs)
  const userDetails = useSelector(state => state.profile?.userDetails)

  const [prescriptionList, setPrescriptionList] = useState([])
  const [ssoRedirectAction, setSsoRedirectAction] = useState(null)
  const [isMoreOptionPressed, setIsMoreOptionPressed] = useState(false)
  const [isSSONoteVisible, setIsSSONoteVisible] = useState(false)
  const [ssoLinkName, setSSOLinkName] = useState('')
  const [ssoLink, setSSOLink] = useState('')

  // we should hide the PBM option if the user is not insured
  // we are showing the option is the user is hmo, insured and termed and cancelled status
  // if member is termed or cancelled, we should disable rx reimbursement option and drug alternative option
  const shouldShowPBM = !isMemberInsuranceNotActivated(userDetails)
  const isCancelledOrTermed =
    isMemberCanceled(userDetails) || isMemberTermed(userDetails)

  const isNHMember =
    userDetails?.hmo_data?.hmo_provider === HMOProviders.NIRVANA
  const prescriptionsReimbursementPdf =
    authenticatedLinks?.hmo_forms?.navitus_reimbursement_pdf

  const nhPrescriptionsReimbursementPdf =
    authenticatedLinks?.nirvana_member_data?.reimbursement_pdf

  useEffect(() => {
    dispatch(
      Actions.createAction(Actions.SET_PRESCRIPTIONS_BY_ID, {
        id: selectedMemberId,
      })
    )
  }, [selectedMemberId])

  useEffect(() => {
    if (isMoreOptionPressed) {
      setTimeout(() => {
        setIsMoreOptionPressed(false)
      }, 5000)
    }
  }, [isMoreOptionPressed])

  useEffect(() => {
    const data = prescriptions?.map(p => {
      const fileId = p?.file || 0
      return {
        issueDate: dayjs(p?.creationDate).format('DD MMM YYYY'),
        title: p?.type,
        doctor: p?.doctor?.speciality
          ? `Dr. ${p?.doctor?.firstName} ${p?.doctor?.lastName} | ${p?.doctor?.speciality}`
          : `Dr. ${p?.doctor?.firstName} ${p?.doctor?.lastName}`,
        dateRange: $t('common.validUntil', {
          expiredDate: dayjs(p?.expirationDate).format('DD MMM YYYY'),
        }),
        isPressable: !!fileId,
        onClick: () =>
          dispatch(
            Actions.createAction(Actions.NAVIGATED_TO, `${url}/${fileId}`)
          ),
        patientId: p?.patient,
      }
    })
    setPrescriptionList(data)
  }, [prescriptions])

  const onMoreOptionPressed = () => {
    // scroll to first action option using ref
    moreOptionRef?.current?.scrollIntoView({ behavior: 'smooth' })
    setIsMoreOptionPressed(true)
  }

  const onOptionPressed = ({ action, elementName }) => {
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.click,
        eventName: event.clickEvent.click,
        element: elementName,
      })
    )

    setSsoRedirectAction(action)
  }

  const getEmptyTitle = () => {
    return (
      <div className="empty-prescriptions">
        <div className="title"> {$t('pages.prescriptions.emptyTitle')} </div>
        <div className="subtitle">
          {' '}
          {isNHMember
            ? $t('pages.prescriptions.emptySubtitle2024')
            : $t('pages.prescriptions.emptySubtitle')}{' '}
        </div>
      </div>
    )
  }

  const navigateToPharmacySSOLink = () => {
    // navigate to navitus portal page after alert approved
    setSSOLinkName(BenefitTypeNames.PHARMACY)
    setSSOLink(authenticatedLinks?.[SSO]?.medication_portal)
    setIsSSONoteVisible(true)
  }

  const openRXReimbursementPdf = () => {
    if (isNHMember) {
      window.open(nhPrescriptionsReimbursementPdf, '_blank')
    } else {
      window.open(prescriptionsReimbursementPdf, '_blank')
    }
  }

  return (
    <div className="my-prescriptions">
      <Switch>
        <Route exact path={path}>
          <Page
            title={$t('sideMenu.prescriptions')}
            className="grey"
            displayManageFamily
          >
            {!!prescriptionList?.length && shouldShowPBM && (
              <ActionButton
                label={$t('pages.prescriptions.moreOptions')}
                className="more-btn center"
                size={32}
                onPress={onMoreOptionPressed}
                displayArrow={false}
              />
            )}
            <ListView
              data={prescriptionList}
              dataName="prescription"
              isLoading={isLoading}
              emptyTitle={
                shouldShowPBM
                  ? getEmptyTitle()
                  : $t('pages.prescriptions.emptyTitle')
              }
              category={$t('sideMenu.prescriptions')?.toLowerCase()}
            />
            {shouldShowPBM && (
              <div
                className="more-options"
                data-animate={isMoreOptionPressed}
                ref={moreOptionRef}
              >
                {isNHMember ? (
                  <ActionButton
                    label={$t('pages.prescriptions.other')}
                    image={Other}
                    className="more-btn trackable"
                    size={32}
                    onPress={() =>
                      onOptionPressed({
                        // todo: change this for NH to show a pdf instead (part of 2024 members feature)
                        action: Actions.NH_PAGE_PRESCRIPTIONS_PRESSED,
                        elementName: 'Other prescriptions',
                      })
                    }
                    displayArrow={true}
                    description={$t('pages.prescriptions.otherTooltip')}
                    arrowIcon="icon-export"
                  />
                ) : (
                  <ActionButton
                    label={$t('pages.prescriptions.pharmacyInformation')}
                    image={Other}
                    className="more-btn trackable"
                    size={32}
                    onPress={navigateToPharmacySSOLink}
                    displayArrow={true}
                    description={$t('pages.prescriptions.pharmacyInfoSubtitle')}
                    arrowIcon="icon-export"
                  />
                )}
                <ActionButton
                  label={$t('pages.prescriptions.Rxreimbursement')}
                  image={Reimbursement}
                  className="more-btn trackable"
                  size={32}
                  disabled={isCancelledOrTermed}
                  onPress={openRXReimbursementPdf}
                  displayArrow={true}
                  arrowIcon="icon-export"
                />
                <ActionButton
                  label={$t('pages.prescriptions.pharmacy')}
                  image={Pharmacy}
                  className="more-btn trackable"
                  size={32}
                  onPress={() =>
                    dispatch(
                      Actions.createAction(
                        Actions.NAVIGATED_TO,
                        routes.PROFILE_ROUTE.PHARMACY
                      )
                    )
                  }
                  displayArrow={true}
                  arrowIcon="icon-export"
                />
              </div>
            )}
            <SSOAlert
              isSSONoteVisible={isSSONoteVisible}
              setIsSSONoteVisible={setIsSSONoteVisible}
              ssoLink={ssoLink}
              ssoLinkName={ssoLinkName}
            />
          </Page>
        </Route>
        <Route path={`${path}/:fileId`}>
          <FilePreviewPage />
        </Route>
      </Switch>
    </div>
  )
}

export default Prescription
